import { apiConfig } from '@/config/tokenConfig';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { useEffect, useState } from 'react';

/**
 * useAccessToken Hook
 * Provides a function to get a silent access token using MSAL
 * Decodes the token to extract groups and stores them in local storage
 * Handles token expiration and automatically redirects to login if needed
 * @returns {Object} - An object containing the getSilentToken function
 */
const useAccessToken = () => {
  const { instance, accounts } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(null);

  interface IToken extends JwtPayload {
    groups: string[];
  }

  /**
   * Gets a silent access token using MSAL
   * Decodes the token to extract groups and stores them in local storage
   * If silent token acquisition fails, redirects to login
   */
  const getSilentToken = async () => {
    const tokenRequest = {
      account: accounts[0],
      scopes: apiConfig.resourceScopes,
    };

    try {
      const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
      const tokenDecoded: IToken = jwtDecode(tokenResponse.accessToken);
      localStorage.setItem('groups', JSON.stringify(tokenDecoded.groups));

      return tokenResponse.accessToken;
    } catch (error) {
      // If token cannot be acquired silently (e.g., expired), redirect to login
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('Token expired or interaction required, redirecting to login...');
        await instance.loginRedirect(tokenRequest);
      }
      throw error;
    }
  };

  useEffect(() => {
    const fetchToken = async () => {
      const token = await getSilentToken();
      console.log('Fetched token:', token);
      setAccessToken(token);
    };
    fetchToken();
  }, []);

  return { getSilentToken, accessToken };
};

export default useAccessToken;
